// ============================================
// GARANTIASLISTA.JS — lista de certificados de garantia (pestaña "Garantias")
// ============================================
// Reusable en la ficha del expediente y en las fichas de cliente (grupo, razon social,
// sucursal, persona). Lee GET /api/garantias?<filtro>. Muestra por certificado: folio GAR,
// codigo, cotizacion de referencia, estado (pendiente de entrega / vigente / por vencer /
// vencida), inicio y lineas con su garantia y vencimiento. Sin precios (no es documento de
// dinero). Responsive: tarjetas apiladas; la tabla de lineas va en su propio scroll.

import React, { useEffect, useState } from 'react';
import { ShieldCheck, ExternalLink } from 'lucide-react';
import { API_BASE_CLI, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE, estiloTarjeta } from './helpersClientes';
import { ligaPublica } from '../config/api';

function authHeaders() {
  const t = localStorage.getItem('beep_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const ESTADO = {
  pendiente_entrega: { label: 'Pendiente de entrega', bg: '#F3F5F8', color: '#5E666E' },
  vigente:           { label: 'Vigente',              bg: '#E8FAF5', color: '#16a34a' },
  por_vencer:        { label: 'Por vencer',           bg: '#FFF6E5', color: '#9A5B00' },
  vencida:           { label: 'Vencida',              bg: '#FDE8E8', color: '#dc2626' },
};

export function fmtFechaCorta(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}
export function fmtGarantiaTexto(meses, dias) {
  if (dias && !meses) return `${dias} días`;
  const m = Number(meses) || 0;
  if (!m) return '—';
  if (m % 12 === 0) return m === 12 ? '1 año' : `${m / 12} años`;
  return `${m} meses`;
}

export default function GarantiasLista({ filtro, compacto = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const qs = filtro ? Object.entries(filtro).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&') : '';

  useEffect(() => {
    if (!qs) { setData([]); return; }
    let vivo = true;
    fetch(`${API_BASE_CLI}/api/garantias?${qs}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(j => { if (!vivo) return; if (j.ok) setData(j.garantias || []); else setError(j.error || 'No se pudieron leer las garantias'); })
      .catch(e => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [qs]);

  if (error) return <div style={{ fontSize: 12, color: '#dc2626' }}>{error}</div>;
  if (!data) return <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Cargando garantías…</div>;
  if (!data.length) {
    return (
      <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, lineHeight: 1.5 }}>
        Aún no hay certificados de garantía. Se crean al generar el PDF de una cotización (o al autorizarla) y arrancan en la fecha de entrega del expediente.
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {data.map(g => {
        const est = ESTADO[g.estado] || ESTADO.pendiente_entrega;
        const url = ligaPublica('garantia', g.codigo);   // se le pasa al cliente: direccion publica
        return (
          <div key={g.id} style={{ ...estiloTarjeta, padding: compacto ? 12 : 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <ShieldCheck size={18} strokeWidth={2} color="#265F97" />
              <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>{g.folio}</div>
              <span style={{ padding: '3px 10px', borderRadius: 99, background: est.bg, color: est.color, fontSize: 11, fontWeight: 700 }}>{est.label}</span>
              <a href={url} target="_blank" rel="noreferrer" style={{ marginLeft: 'auto', fontSize: 12, color: '#265F97', display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 32 }}>
                Ver registro oficial <ExternalLink size={13} />
              </a>
            </div>
            <div style={{ fontSize: 12, color: COLOR_GRIS, display: 'flex', gap: '4px 16px', flexWrap: 'wrap', marginBottom: 8 }}>
              <span>Código <strong style={{ fontFamily: 'monospace' }}>{g.codigo}</strong></span>
              {g.cotizacion_folio && <span>Cotización <strong>{g.cotizacion_folio}</strong></span>}
              {g.expediente_folio && <span>Expediente <strong>{g.expediente_folio}</strong></span>}
              <span>Inicio: <strong>{g.fecha_inicio ? fmtFechaCorta(g.fecha_inicio) : 'fecha de entrega'}</strong></span>
              {g.ultimo_vencimiento && <span>Último vencimiento: <strong>{fmtFechaCorta(g.ultimo_vencimiento)}</strong></span>}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 420 }}>
                <thead>
                  <tr style={{ color: COLOR_GRIS_CLARO, textAlign: 'left' }}>
                    <th style={{ padding: '4px 6px', fontWeight: 600 }}>Producto / servicio</th>
                    <th style={{ padding: '4px 6px', fontWeight: 600 }}>Garantía</th>
                    <th style={{ padding: '4px 6px', fontWeight: 600 }}>Vence</th>
                    <th style={{ padding: '4px 6px', fontWeight: 600 }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {(g.lineas || []).map(l => {
                    const el = ESTADO[l.estado] || ESTADO.pendiente_entrega;
                    const esServ = l.tipo === 'servicio' || l.tipo === 'cable';
                    return (
                      <tr key={l.id} style={{ borderTop: `1px solid ${COLOR_BORDE}`, background: esServ ? '#EAF7F0' : 'transparent' }}>
                        <td style={{ padding: '6px' }}>
                          <div style={{ fontWeight: 600, color: COLOR_OSCURO }}>{l.producto_nombre}</div>
                          <div style={{ fontSize: 11, color: esServ ? '#1E7A4C' : '#265F97', fontWeight: 600 }}>
                            {esServ ? 'BEEP MÉXICO · Servicio' : [l.marca, l.modelo].filter(Boolean).join(' · ')}
                          </div>
                        </td>
                        <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{fmtGarantiaTexto(l.garantia_meses, l.garantia_dias)}<div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>{l.origen}</div></td>
                        <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{l.fecha_vencimiento ? fmtFechaCorta(l.fecha_vencimiento) : 'desde la entrega'}</td>
                        <td style={{ padding: '6px' }}><span style={{ padding: '2px 8px', borderRadius: 99, background: el.bg, color: el.color, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{el.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
